<template lang="pug">

  div

    v-dialog(v-model="showTimesheetModal" max-width="320px" :persistent="isLoadingGroupUsers === true || buildingReport === true")

      .dialogHeader

        .dialogHeaderTitle
          | Simular Folha De Ponto

        .dialogHeaderClose
          .dialogCloseButton.mdi.mdi-close(@click="close()")

      .dialogBody

        .dialogBodyContent(v-if="isLoadingGroupUsers")

          .dialogLoader
            .dialogLoaderInner
              .dialogLoaderCircle
              span.dialogLoaderText {{ loadingMessage }}

        .dialogBodyContent(v-else)

          //-
            - MONTH AND YEAR PICKER
            -

          .modalTimesheetField.mb-4
            label.csLabel
              span(v-html="'Selecione o mês e ano'")
            .csFields.csFields--date
              date-picker(v-model="dateRange" @change="onDateChange" :clearable="false" :disabled-date="disableDatesCondition" range valueType="format" format="DD/MM/YYYY" :lang="lang" class="fieldSimulatedTimesheetMonthYear" :disabled="buildingReport")

          //-
            - USERS LIST
            -

          .modalTimesheetField.mb-6(v-if="users.length === 0")
            span.err
              | Nenhum profissional foi encontrado nesse período.

          .modalTimesheetField.mb-6(v-else)
            sos-checkbox(v-if="users.length > 1" id="simulado_todos" label="" @click="onAllCheckboxClick" :checked="checkboxAllUsers" :disabled="buildingReport").modalTimesheetGenerateButton

            label.csLabel
              span {{ users.length === 1 ? 'Selecione o usuário' : 'Selecione os usuários' }}
            .csFields
              users-list-component(:users="users" @click="onClickUser")

          //-
            - USER OPTIONS
            -

          .modalTimesheetField--showHourlyRate.mb-5(v-if="users.length > 0")
            label.csLabel.mb-2
              span(v-html="'Opções'")
            .csFields
              sos-checkbox(id="mostrar_valor_hora" label="Exibir Valor hora" @click="onShowHourlyRateClick" :checked="showHourlyRate" :disabled="buildingReport")

          //-
            - ACTIONS
            -

          div(v-if="users.length > 4")
            .inline-block.footerLeft
              span.usersCount {{ users.length }} usuários
            .inline-block.footerRight
              sos-button(@buttonClick="generateTimesheet()" buttonLabel="Gerar" color="success" buttonIcon="mdi-arrow-right" iconPosition="right" buttonIconSize="15" size="medium" :loading="buildingReport" :disabled="selectedUsers.length === 0")

          .text-right(v-else)
            sos-button(@buttonClick="generateTimesheet()" buttonLabel="Gerar" color="success" buttonIcon="mdi-arrow-right" iconPosition="right" buttonIconSize="15" size="medium" :loading="buildingReport" :disabled="selectedUsers.length === 0")

    //- DIALOG PARA CONFIRMAÇÃO OU INFORMAÇÃO
    confirm-dialog(ref="cdConfirmModal" :title="cdParameters.title" :text="cdParameters.text" :buttonPosition="cdParameters.buttonPosition" :width="cdParameters.width" @close="cdMessageResponse")

    //- DIALOG PARA INFORMAR LIMITAÇÃO DE FUNCIONALIDADE E SITUAÇÃO DO PLANO ATUAL
    subscription-modal(:active="subscriptionModal.active" :titulo="subscriptionModal.title" :text="subscriptionModal.message" @close="closeSubscriptionModal")

</template>

<script>

  import { freeVersion } from '@/constants'
  import { apiCheckReportStatus, apiOpenReport } from '@/services/report'
  import { getUserByID } from '@/services/firebase'

  import { isValidDate, parseDate } from '@/mixins/utils.js'
  import { apiGetGroupStatistics } from '@/services/duties'

  import UsersListComponent from '@/components/UsersListComponent'
  import SubscriptionModal from '@/modals/SubscriptionModal'
  import ConfirmDialog from '@/modals/ConfirmDialog'
  import SosButton from '@/components/SosButton'
  import SosCheckbox from '@/components/SosCheckbox'

  import GroupMixin from '@/mixins/group.mixin'
  import UserMixin from '@/mixins/user.mixin'
  import SubscriptionModalMixin from '@/mixins/subscription.modal.mixin'
  import confirmDialogMixin from '@/mixins/cd.mixin.js'

  import { mapState } from 'vuex'

  import DatePicker from 'vue2-datepicker'
  import 'vue2-datepicker/index.css'

  import moment from 'moment'
  import 'moment/locale/pt-br'

  moment.locale('pt-BR')

  //
  // USERFUL FUNCTIONS
  //

  const buildSubscriptionModalContent = userSession => {
    const planName = userSession.planName.name

    const talkToOurSupport = `<a href="https://api.whatsapp.com/send?phone=5513992061010&text=Olá, estou no ${planName} e não consigo simular a folha de ponto. Preciso de ajuda." target="_blank">suporte</a>`
    const textSupport = `<p>Por favor, verifique a situação do seu plano com o nosso ${talkToOurSupport}.</p>`

    if (userSession.planOrigin === 'CUSTOM') {
      // Plano personalizado
      return { title: 'Verifique seu plano', message: `<p>Você está no ${planName} personalizado, mas a folha de ponto simulada não está liberada.</p>${textSupport}` }
    } else if (userSession.planOrigin === 'COUPON') {
      // Cupom ativo
      return { title: 'Verifique seu plano', message: `<p>Você tem um cupom ativo e está no ${planName}, mas a folha de ponto simulada não está liberada.</p>${textSupport}` }
    } else if (userSession.planOrigin === 'VINDI' && userSession.vindi.paymentStatus === 'paid') {
      // Assinatura ativa na vindi
      return { title: 'Faça um upgrade', message: `<p>Você está no ${planName}.</p><p>Para gerar a folha de ponto simulada, assine o Plano Silver ou superior.</p><p>Para mais informações, converse o nosso ${talkToOurSupport}.</p>` }
    } else if (userSession.planOrigin === 'VINDI' && userSession.vindi.paymentStatus !== 'paid') {
      // Assinatura ativa, mas com alguma pendência ou erro na vindi
      return { title: 'Verifique sua assinatura', message: `<p>Você é assinante do ${planName}, mas ele não está ativo e a folha de ponto simulada não está liberada.</p>${textSupport}` }
    } else {
      if (!!userSession.vindi === true) {
        // Plano da vindi expirado
        return { title: 'Verifique sua assinatura', message: `<p>Você assinou o ${userSession.vindi.planName.name}, mas ele expirou.</p><p>Agora você está no ${planName} e a folha de ponto simulada não está liberada.</p>${textSupport}` }
      } else {
        // Plano gratuíto ou alcançou alguma condição desconhecida
        return { title: 'Verifique seu plano', message: `<p>Você está no ${planName} e a folha de ponto simulada não está liberada.</p>${textSupport}` }
      }
    }
  }

  //
  // COMPONENT
  //

  export default {
    name: 'ModalTimesheetSimulation',

    components: {
      UsersListComponent,
      SubscriptionModal,
      ConfirmDialog,
      'sos-checkbox': SosCheckbox,
      'sos-button': SosButton,

      // Third party
      'date-picker': DatePicker,
    },

    mixins: [
      GroupMixin,
      UserMixin,
      SubscriptionModalMixin,
      confirmDialogMixin,
    ],

    data () {
      return {
        showTimesheetModal: false,

        buildingReport: false,

        checkboxAllUsers: false,
        showHourlyRate: false,

        isLoadingGroupUsers: false,
        users: [],

        // SELETOR DE PERÍODO
        dateRange: [],
        changedDateRange: [],

        lang: {
          formatLocale: {
            // MMMM
            months: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
            // MMM
            monthsShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
            // dddd
            weekdays: ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'],
            // ddd
            weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
            // dd
            weekdaysMin: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'],
            // weekdaysMin: ['Do', 'Se', 'Te', 'Qu', 'Qu', 'Se', 'Sa'],
          },
          yearFormat: 'YYYY',
          monthFormat: 'MMMM',
        },
      }
    },

    computed: {
      ...mapState(['userSession', 'groupUsers']),

      loadingMessage: function () {
        return this.isLoadingGroupUsers ? 'Carregando usuários...' : (this.buildingReport ? 'Gerando folha de ponto...' : 'Carregando...')
      },

      selectedUsers: function () {
        return !!this.users === true && this.users.length > 0 ? this.users.filter(user => user.checked).map(user => user.uid) : []
      },
    },

    watch: {
      changedDateRange: {
        immediate: true,
        handler (newRange, oldRange)
        {
          if (
            !!this.groupID === true &&
            Array.isArray(newRange) &&
            newRange.length === 2 &&
            isValidDate(newRange[0]) &&
            isValidDate(newRange[1])
          ) {
            const firstDateObj = parseDate(newRange[0])
            const lastDateObject = parseDate(newRange[1])
            const differenceInDays = Math.floor((lastDateObject - firstDateObj) / (1000 * 60 * 60 * 24))

            if (differenceInDays > 62) {
              this.dateRange = oldRange
              this.showMessage('Atenção!', 'Você pode selecionar até dois meses')
            } else {
              this.loadGroupUsers(this.groupID, newRange[0], newRange[1])
            }
          }
        },
      },

      showTimesheetModal: {
        immediate: true,
        handler: function (showTimesheetModal, oldShowTimesheetModal)
        {
          if (oldShowTimesheetModal === true && showTimesheetModal === false) this.reset()
        },
      },
    },

    beforeDestroy () {
      this.clear()
    },

    methods: {
      //
      //
      //
      // DESABILITA TODAS AS DATAS ANTERIORES A 2016
      // CONSIDERAR FUSO HORÁRIO?
      //
      //
      //

      disableDatesCondition (date, _ /* currentValue */)
      {
        return date < new Date(2016, 0, 1)
      },

      //
      //
      //

      reset () {
        this.showTimesheetModal = false

        this.buildingReport = false

        this.checkboxAllUsers = false
        this.showHourlyRate = false

        this.isLoadingGroupUsers = false
        this.users = []

        // SELETOR DE PERÍODO
        this.dateRange = []
        this.changedDateRange = []

        // cd.mixin.js
        this.cdParameters = { title: '', text: '', buttonPosition: 'right', width: 330 }
      },

      clear () {
        this.reset()

        delete this.showTimesheetModal

        delete this.buildingReport

        delete this.checkboxAllUsers
        delete this.showHourlyRate

        delete this.isLoadingGroupUsers
        delete this.users

        // SELETOR DE PERÍODO
        delete this.dateRange
        delete this.changedDateRange

        // cd.mixin.js
        delete this.cdParameters
      },

      //
      //
      //

      // JESUS - Testar grupo sem usuários ou com 1 usuário

      async loadGroupUsers (groupID, firstDate, lastDate)
      {
        // [24/01/25] console.log('::: loadGroupUsers:', groupID, firstDate, lastDate)

        this.isLoadingGroupUsers = true

        const [firstDateDay, firstDateMonth, firstDateYear] = String(firstDate).split('/').map(Number)
        const [lastDateDay, lastDateMonth, lastDateYear] = String(lastDate).split('/').map(Number)

        const startOfMonth = moment.utc([firstDateYear, firstDateMonth - 1, firstDateDay, 0, 0, 0, 0]).startOf('day').unix() * 1000
        const endOfMonth = moment.utc([lastDateYear, lastDateMonth - 1, lastDateDay, 0, 0, 0, 0]).endOf('day').unix() * 1000

        const userKeys = []

        const shifts = await apiGetGroupStatistics(groupID, { start: startOfMonth, end: endOfMonth })

        if (shifts.length > 0)
        {
          shifts.forEach(([shiftID, shift]) => {
            if (
              !!shift === true &&

              !!shift.uid_pegou === true &&
              String(shift.uid_pegou).trim().length > 0 &&

              !!shift.data_inicio === true

              // !!shift.data_inicio === true &&
              // shift.data_inicio >= startOfMonth &&
              // shift.data_inicio <= endOfMonth
            )
            {
              const userTime = !!shift.start_location === true && !!shift.end_location === true ? 'START_END' : (!!shift.start_location === true ? 'START' : false)

              const foundUserIndex = userKeys.findIndex(user => user.userID === String(shift.uid_pegou).trim())

              if (foundUserIndex !== -1)
              {
                if (
                  (userKeys[foundUserIndex].userTime === false && userTime !== false) ||
                  (userKeys[foundUserIndex].userTime === 'START' && userTime === 'START_END')
                ) {
                  userKeys[foundUserIndex].userTime = userTime
                }
              }
              else
              {
                userKeys.push({ userID: String(shift.uid_pegou).trim(), userTime })
              }
            }
          })
        }

        const usersList = userKeys.length > 0 ? await Promise.all(
          userKeys.map(async user => ({ ...user, ...await getUserByID(user.userID) })).filter(user => !!user === true && typeof user === 'object')
        ) : []

        if (usersList.length > 0) usersList.sort((a, b) => a.nome.localeCompare(b.nome))

        this.users = usersList.map(user => ({ ...user, checked: false })) // .slice(0, 1)

        this.checkboxAllUsers = false

        this.isLoadingGroupUsers = false

        //
        // CACHE
        //

        localStorage.setItem(`@sosplantao_simulated_timesheet_${this.groupID}`, JSON.stringify({ firstDate, lastDate }))
      },

      //
      //
      //

      onDateChange (dates)
      {
        // [24/01/25] console.log('::: onDateChange:', dates)

        this.changedDateRange = dates
      },

      //
      //
      //

      goToPlanos () {
        this.$router.push('/admin/assinatura')
      },

      //
      //
      //

      close ()
      {
        if (!!this.buildingReport || !!this.isLoadingGroupUsers) return false

        this.showTimesheetModal = false

        // // this.reset()
        // this.clear()
      },

      //
      //
      //

      open ()
      {
        //
        // MESSAGE TO FREE USER'S
        //

        if (freeVersion === false && this.userSession.userCapabilities.folhaDePonto !== true)
        {
          const subscriptionModalContent = buildSubscriptionModalContent(this.userSession)

          this.openSubscriptionModal(subscriptionModalContent.title, subscriptionModalContent.message)

          return false
        }

        //
        // CACHED FILTERS
        //

        let loadedFilter = null

        const filters = localStorage.getItem(`@sosplantao_simulated_timesheet_${this.groupID}`)

        if (!!filters === true) {
          try {
            loadedFilter = JSON.parse(filters)
          } catch (e) {
            // [24/01/25] console.log('::: TIMESHEET > localStorage > DELETE:', `@sosplantao_simulated_timesheet_${this.groupID}`)
            localStorage.removeItem(`@sosplantao_simulated_timesheet_${this.groupID}`)
          }
        }

        //
        //
        //

        // Initialize Moment JS
        moment.locale('pt-BR')

        // Aplica mês e ano vigentes
        const firstDate = !!loadedFilter === true && !!loadedFilter.firstDate === true ? (
          loadedFilter.firstDate
        ) : (
          moment().startOf('month').format('DD/MM/YYYY')
        )

        const lastDate = !!loadedFilter === true && !!loadedFilter.lastDate === true ? (
          loadedFilter.lastDate
        ) : (
          moment().endOf('month').format('DD/MM/YYYY')
        )

        this.dateRange = [firstDate, lastDate]
        this.changedDateRange = [firstDate, lastDate]

        // [24/01/25] console.log('::: SIMULAR FOLHA DE PONTO:', this.dateRange)

        //
        // SHOW
        //

        this.showTimesheetModal = true
      },

      //
      //
      //

      async generateTimesheet ()
      {
        if (!!this.buildingReport === true) return false

        //
        // VALIDATE DATE RANGE
        //

        const isValid = Array.isArray(this.dateRange) && this.dateRange.length === 2 && isValidDate(this.dateRange[0]) && isValidDate(this.dateRange[1])

        if (!isValid) {
          return this.showMessage('Atenção!', 'Selecione o período da folha de ponto')
        }

        //
        // VALIDATE DATE RANGE SIZE
        //

        /* const firstDateObj = parseDate(this.dateRange[0])
        const lastDateObject = parseDate(this.dateRange[1])
        const differenceInDays = Math.floor((lastDateObject - firstDateObj) / (1000 * 60 * 60 * 24))

        if (differenceInDays > 62) return this.showMessage('Atenção!', 'Você pode selecionar até dois meses.') */

        //
        //
        //

        this.buildingReport = true

        try
        {
          const selectedUsersIds = this.users.filter(user => user.checked).map(user => user.uid).join(',')

          const checkEndpoint = `checktimesheet/${selectedUsersIds}/${this.groupID}/${this.dateRange[0]}/${this.dateRange[1]}/simulate`
          const generateUrl = `timesheet/${selectedUsersIds}/${this.groupID}/${this.dateRange[0]}/${this.dateRange[1]}/simulate/${this.showHourlyRate ? 'true' : 'false'}`

          const response = await apiCheckReportStatus(checkEndpoint)

          if (response.code !== 1) {
            this.showMessage(response.title, response.message)
          } else {
            apiOpenReport(generateUrl)
          }
        }
        catch (error)
        {
          this.showMessage('Erro!', 'Não foi possível gerar o relatório. Por favor, atualize a página e tente novamente ou fale com o nosso suporte.')
        }

        this.buildingReport = false
      },

      onClickUser (user)
      {
        if (!!this.buildingReport === true) return false

        const { uid: userID } = user

        this.users = this.users.map(user => (user.uid === userID ? { ...user, checked: !user.checked } : user))

        this.checkboxAllUsers = this.users.filter(user => user.checked === true).length === this.users.length

        // console.log('::: onClickUser:', this.users.filter(user => user.checked).map(user => user.userName))
      },

      onAllCheckboxClick ()
      {
        if (!!this.buildingReport === true) return false

        this.checkboxAllUsers = !this.checkboxAllUsers

        this.users = this.users.map(user => ({ ...user, checked: this.checkboxAllUsers }))
      },

      onShowHourlyRateClick ()
      {
        if (!!this.buildingReport === true) return false

        this.showHourlyRate = !this.showHourlyRate
      },
    },
  }

</script>

<style lang="scss">

  .modalTimesheetField--showHourlyRate
  {
    font-size: 0;

    .csLabel {
      display: inline-block;
      height: auto;

      line-height: 15px;
    }

    .sosCheckboxLabel {
      color: #495057;
      font-size: 15px !important;
      font-weight: 500;
      // line-height: 20px !important;
      padding-left: 7px !important;
    }
  }

  .fieldSimulatedTimesheetMonthYear {
    width: 100%;

    .mx-datepicker {
      width: 100%;
    }
  }

</style>

<style lang="scss" scoped>

  .err {
    color: indianred;
    font-size: 15px;
    font-weight: 400;
    line-height: 1;
  }

  .modalTimesheetField {
    position: relative;
    width: 100%;
  }

  .modalTimesheetGenerateButton {
    position: absolute;
    right: 6px;
    top: 5px;
  }

  .inline-block {
    display: inline-block;
    vertical-align: middle;
  }

  .usersCountArea {
    line-height: 0;
  }

  .usersCount {
    font-size: 15px;
    line-height: 1;
  }

  .footerLeft {
    width: 50%;

    text-align: left;
  }

  .footerRight {
    width: 50%;
    text-align: right;
  }

</style>
